/**
 * 任务 19 ·「元指令被当正文画进图」的识别层(2026-07-31)。
 *
 * ═══ 现象 ═══════════════════════════════════════════════════════════════════
 * 测试同学把「超大圆体字」写进文案字段 → 这五个字被**原样印在成品上**。
 * 同时她的字体档选的是「手写」,出来是毛笔体 —— 她想要的「圆润」档其实一直存在,
 * 只是她不知道有这个开关,于是改用"写给 AI 听"的方式表达,而文案字段对元指令**零拦截**。
 *
 * ★根因不是文字渲染(所以**不受**「文字不处理、交给 AI 画」那条决定影响):
 *   是**写进内容字段的排版要求一律被当正文**。同一句话,放对地方是参数,放错地方是正文。
 *
 * ═══ 判据纪律:宁可漏判不可错判 ═══════════════════════════════════════════════
 * 与 `overloadHint` 同一条纪律,但这里更严 —— 误判的代价是**把用户真想印的字劝掉**。
 *
 * ① **只认强排版词**,不认口语。「手写」单独出现**不算**(「客户手写感谢信」是正文);
 *    必须是「手写体 / 手写字 / 毛笔字」这种一看就是在说字形的写法。
 * ② **看后接字**,不看前缀 —— 「手写体验」里的「手写体」不是字体
 *    (同型教训:长图合规判据也是落在后接字上,不是前缀)。
 * ③ 命中后**不自动改任何东西**,只问一句。用户说"照原样印上去"就照印 ——
 *    卖点是用户的,字也是用户的,我们无权替他删([[feedback]] 同 overloadHint ③)。
 *
 * ═══ 为什么分两类 ═══════════════════════════════════════════════════════════
 * - `font`:有**真去处**(字体档 5 选 1),所以能一键设过去,并把那段话从正文摘掉。
 * - `styling`:「鲜艳点」这类没法映射到某一份具体配色,但它同样不该被印。
 *   去处是「特别要求」(`extraConstraint`)—— 那里的话会进提示词当约束,而**不当正文**。
 *   ★不给它硬凑一个配色档:凑出来的档位就是下一个 `fidelity`(一个看着能调、实际不对的旋钮)。
 */

/** 命中一段元指令后,能提供的处置方向。 */
export type MetaInstructionKind = "font" | "styling";

export interface MetaInstructionHint {
  /** 命中的那一段**原文**(逐字回显给用户看,也用于一键处置时精确摘除)。 */
  matched: string;
  kind: MetaInstructionKind;
  /** kind==="font" 时:目标字体档 key(`fontStyleOptions` 的 key)。 */
  fontKey?: string;
  /** kind==="font" 时:目标字体档中文名(提示里说人话用)。 */
  fontLabel?: string;
}

/**
 * 「体」字后面接这些字 = 另一个词,不是字体。
 *
 * ★这条守的是「手写体验」——它含「手写体」三个字,但那是"体验"。
 *   判据落在**后接字**上,不落在前缀上。
 */
const NOT_A_FONT_SUFFIX = /^[验会育制系重检现面力温型贴]/;

/** 尺寸前缀(命中时一并收进 matched,否则摘掉字体词会剩下一个孤零零的「超大」)。 */
const SIZE_PREFIX = "(?:超大|特大|大号|小号|加粗)?";

interface FontRule {
  /** 强排版写法 —— 一看就是在说字形,不会是海报正文。 */
  terms: string[];
  fontKey: string;
  fontLabel: string;
}

/** 与 `fontStyleOptions.FONT_STYLES` 的 key 一一对应(那边改了 key,这里的单测会红)。 */
const FONT_RULES: readonly FontRule[] = [
  {
    terms: ["圆体字", "圆体", "圆润字体", "圆润的字", "圆角字体"],
    fontKey: "rounded",
    fontLabel: "圆润",
  },
  {
    terms: ["手写体", "手写字", "毛笔字", "书法字", "手写风格的字"],
    fontKey: "handwritten",
    fontLabel: "手写",
  },
  { terms: ["宋体", "衬线体", "衬线字", "明朝体"], fontKey: "serif", fontLabel: "衬线" },
  { terms: ["无衬线", "黑体字", "黑体"], fontKey: "sans", fontLabel: "现代无衬线" },
];

/**
 * 非字体的观感要求 —— 形状都是「XX 点 / XX 一点 / XX 一些」,是**请求**不是文案。
 *
 * ★只收这种"带程度后缀"的写法:光一个「高级」可能是正文(「高级定制」),
 *   「高级一点」不可能是要印在图上的字。
 */
const STYLING_TERMS = [
  "鲜艳",
  "明亮",
  "大气",
  "简约",
  "高级",
  "干净",
  "醒目",
  "柔和",
  "低调",
  "有质感",
];
const STYLING_DEGREE = "(?:一点|一些|点儿|点)";

/** 命中位置的后一个字是不是"把它变成了另一个词"。 */
function isFalseFontMatch(text: string, index: number, term: string): boolean {
  if (!term.endsWith("体")) return false;
  const next = text.slice(index + term.length, index + term.length + 1);
  return NOT_A_FONT_SUFFIX.test(next);
}

/**
 * 从一段文案里找出**第一处**元指令。返回 null = 没找到(或不确定 → 不打扰)。
 *
 * 只返第一处:提示是一句话不是清单;用户处置完这一处会重新检测,自然接上下一处。
 */
export function detectMetaInstruction(text: string): MetaInstructionHint | null {
  const t = text || "";
  if (!t.trim()) return null;

  let best: (MetaInstructionHint & { index: number }) | null = null;

  for (const rule of FONT_RULES) {
    for (const term of rule.terms) {
      const re = new RegExp(SIZE_PREFIX + term, "g");
      let m: RegExpExecArray | null;
      while ((m = re.exec(t)) !== null) {
        // 命中位置里 term 的起点(matched 可能带尺寸前缀)
        const termIndex = m.index + m[0].length - term.length;
        if (isFalseFontMatch(t, termIndex, term)) continue;
        if (!best || m.index < best.index) {
          best = {
            matched: m[0],
            kind: "font",
            fontKey: rule.fontKey,
            fontLabel: rule.fontLabel,
            index: m.index,
          };
        }
        break;
      }
    }
  }

  for (const term of STYLING_TERMS) {
    const re = new RegExp(term + STYLING_DEGREE, "g");
    const m = re.exec(t);
    if (m && (!best || m.index < best.index)) {
      best = { matched: m[0], kind: "styling", index: m.index };
    }
  }

  if (!best) return null;
  const { index: _i, ...hint } = best;
  return hint;
}

/**
 * 把命中的那一段从文案里摘掉(用户选了「这不是要印的字」时用)。
 *
 * ★只摘**第一处**且**逐字**摘 —— 与 `detectMetaInstruction` 返的 matched 同一段。
 *   全局替换会误伤用户在别处真想印的同样几个字。
 * ★顺手收拾摘完留下的孤立标点与多余空白:留一个「超大,」在文案里比不摘更难看。
 */
export function stripMetaInstruction(text: string, matched: string): string {
  const at = text.indexOf(matched);
  if (at < 0) return text;
  const out = text.slice(0, at) + text.slice(at + matched.length);
  return out
    .replace(/[ \t]{2,}/g, " ")
    .replace(/^[,,、;;的]+/, "")
    .replace(/([,,、;;])\s*\1+/g, "$1")
    .replace(/[,,、;;]\s*(?=[。.\n]|$)/g, "")
    .trim();
}
